import {
  type FullMessageKey,
  MessageKey,
  type EncodedMessageKey,
  type AnyMessage,
  type Message,
} from '@canvas/protocol-types';

import { reverseEnum } from './type-utils';

/**
 * Map of message keys, mapping the encoded form of each key to its full name.
 */
export const MessageKeyReverse = reverseEnum(MessageKey);

type EncodedRecord<T extends Record<string, any>> = {
  [K in keyof T as `${K extends FullMessageKey ? (typeof MessageKey)[K] : never}`]?: T[K];
};

/**
 * A {@link Message} with each full name key replaced with its encoded form.
 */
export type EncodedMessage = {
  [K in keyof AnyMessage as `${(typeof MessageKey)[K]}`]: AnyMessage[K] extends Record<
    string,
    any
  >
    ? EncodedRecord<AnyMessage[K]>
    : AnyMessage[K];
};

function encodeObjectKeys(
  object: Partial<Record<FullMessageKey, any>>,
): Partial<Record<EncodedMessageKey, any>> {
  const result: Partial<Record<EncodedMessageKey, any>> = {};

  for (const key in object) {
    if (key in MessageKey) {
      const encodedKey = MessageKey[key as FullMessageKey];
      result[encodedKey] = object[key as FullMessageKey];
    } else {
      throw new Error(`Cannot encode key as a mapping is missing: ${key}`);
    }
  }

  return result;
}

/**
 * Encodes the keys of a given message, using the mapping defined by {@link MessageKey}.
 *
 * @param message The message to encode the keys of.
 * @returns The message with each full name key replaced with its encoded form.
 * @throws Throws an error if the message contains keys not defined in
 *   {@link MessageKey}.
 */
export function encodeMessageKeys(message: Message): EncodedMessage {
  const result = encodeObjectKeys(message);

  // Encode data if present
  if (MessageKey.data in result) {
    const data = result[MessageKey.data];
    result[MessageKey.data] = encodeObjectKeys(data);
  }

  return result as EncodedMessage;
}

function decodeObjectKeys(
  object: Partial<Record<EncodedMessageKey, any>>,
): Partial<Record<FullMessageKey, any>> {
  const result: Partial<Record<FullMessageKey, any>> = {};

  for (const encodedKey in object) {
    if (encodedKey in MessageKeyReverse) {
      const key = MessageKeyReverse[encodedKey as EncodedMessageKey];
      result[key] = object[encodedKey as EncodedMessageKey];
    }
  }

  return result;
}

/**
 * Decodes the keys of a given message, using the mapping defined by {@link MessageKey}.
 *
 * @param message The message to decode the keys of.
 * @returns The message with each encoded key replaced with its full name.
 * @throws Throws an error if the message contains key encodings not defined in
 *   {@link MessageKey}.
 */
export function decodeMessageKeys(encodedMessage: EncodedMessage): Message {
  const result = decodeObjectKeys(encodedMessage);

  // Decode data if present
  if ('data' in result) {
    const data = result.data;
    result.data = decodeObjectKeys(data);
  }

  return result as Message;
}
